import type { CollectionSuccessApiResponse } from 'shared/lib/api';
import { Badge } from 'shared/ui/Badge';
import { ClippedText } from 'shared/ui/ClippedText';
import { PaginationWithQueries } from 'shared/ui/Pagination';
import { Sortable } from 'shared/ui/Sortable';
import type { Column } from 'shared/ui/Table';
import { Table } from 'shared/ui/Table';
import {
  ContractorCampaignsSort,
  PendingTooltip,
  CAMPAIGN_STATUS_BADGE_PROPS,
  type ContractorCampaignModel,
  BidTiers,
  getCampaignTypeTableName,
} from 'entities/campaign';
import { useGetContractor } from 'entities/profile';
import type { AllowedTimezones } from 'shared/lib/timezones';
import { getTimezone, timeZonesLegends } from 'shared/lib/timezones';
import { getPendingDate } from 'shared/lib/format';

import { PercentageCellWithBadge } from './PercentageCellWithBadge';
import { RunCampaignCell } from './RunCampaignCell';

const getColumns = (timezone: AllowedTimezones): Column<ContractorCampaignModel>[] => [
  {
    label: 'Campaign',
    key: 'name',
    columnCell: ({ row: campaign }) => {
      return (
        <td className="min-w-[176px]">
          <div className="flex items-center justify-between gap-2">
            <ClippedText
              text={campaign.name}
              tooltip={{
                content: campaign.name,
              }}
            />
            {campaign.pendingChanges ? (
              <PendingTooltip
                isDeactivated={campaign.status === 'deactivated'}
                attributes={campaign.pendingChanges.attributes}
                appliesAt={`${getPendingDate(campaign.pendingChanges.appliesAt)} ${timeZonesLegends[timezone]}`}
                className="ml-2"
              />
            ) : null}
          </div>
        </td>
      );
    },
    headerCell: ({ value }) => (
      <th>
        <Sortable sortKey={ContractorCampaignsSort.campaignName}>{value}</Sortable>
      </th>
    ),
  },
  {
    label: 'Status',
    key: 'status',
    columnCell: ({ row }) => (
      <td className="w-29 2xl:w-1/12">
        <Badge {...CAMPAIGN_STATUS_BADGE_PROPS[row.status]} />
      </td>
    ),
    headerCell: ({ value }) => (
      <th className="w-[157px] min-w-[157px] ">
        <Sortable sortKey={ContractorCampaignsSort.status}>{value}</Sortable>
      </th>
    ),
  },
  {
    label: 'Service',
    key: 'service',
    columnCell: ({ row }) => <td>{row.service.name}</td>,
    headerCell: ({ value }) => (
      <th className="w-[220px] min-w-[220px]">
        <Sortable sortKey={ContractorCampaignsSort.service}>{value}</Sortable>
      </th>
    ),
  },
  {
    label: 'Type',
    key: 'type',
    columnCell: ({ row }) => <td>{getCampaignTypeTableName(row.type)}</td>,
    headerCell: ({ value }) => (
      <th className="w-[149px] min-w-[149px]">
        <Sortable sortKey={ContractorCampaignsSort.type}>{value}</Sortable>
      </th>
    ),
  },
  {
    label: 'Bids',
    key: 'bid',
    columnCell: ({ row }) => (
      <td className="whitespace-nowrap text-center font-medium">
        <BidTiers
          className="flex justify-center"
          serviceName={row.service.name}
          bid={row.bid}
          bidTiers={row.bidTiers}
        />
      </td>
    ),
    headerCell: ({ value }) => (
      <th className="w-[141px] min-w-[141px] text-center">
        <Sortable sortKey={ContractorCampaignsSort.bid}>{value}</Sortable>
      </th>
    ),
  },
  {
    label: 'This week\n(spent / budget)',
    key: 'weeklyBudget',
    columnCell: ({ row }) => (
      <PercentageCellWithBadge
        className="min-w-[141px] truncate text-right"
        title={row.weeklyBudget.spent.amount}
        percent={row.weeklyBudget.spentToBudgetPercent}
        total={row.weeklyBudget.total.amount}
        budget={row.weeklyBudget.budget.amount}
        refund={row.weeklyBudget.refund.amount}
        campaignStatus={row.status}
        overbudgetTooltipContent="Campaign budget has been reached this week. You can increase your budget and the change will take effect immediately."
        otherStatusesTooltipContent="80%+ of your campaign budget has been spent this week. You can increase your budget and the change will take effect immediately."
      />
    ),
    headerCell: ({ value }) => (
      <th className="w-[147px] min-w-[147px] text-right">
        <Sortable sortKey={ContractorCampaignsSort.thisWeekBudgetSpent}>{value}</Sortable>
      </th>
    ),
  },
  {
    label: 'Run Campaign',
    key: 'isActive',
    columnCell: ({ row: campaign }) => <RunCampaignCell {...campaign} />,
    headerCell: ({ value }) => <th className="w-[118px] min-w-[118px] text-center">{value}</th>,
  },
];

interface ContractorCampaignsTableProps {
  data: CollectionSuccessApiResponse<ContractorCampaignModel> | undefined;
  isLoading: boolean;
  onTableRowClick: ({ id }: { id: number }) => void;
}

export function ContractorCampaignsTable({ data, isLoading, onTableRowClick }: ContractorCampaignsTableProps) {
  const contractor = useGetContractor();
  const timezone = contractor.data?.data.timezone ?? getTimezone();
  const columns = getColumns(timezone);

  return (
    <>
      <Table
        isStickyToTopPage
        hasRowCursorPointer
        columns={columns}
        data={data?.data}
        isLoading={isLoading}
        rowKey={(data) => data.id}
        onRowClick={onTableRowClick}
      />
      <PaginationWithQueries
        numOfItems={data?.meta.total}
        initialPage={data?.meta.page}
        initialItemsPerPage={data?.meta.limit}
        className="mt-6"
      />
    </>
  );
}
